import { useState, useEffect, useRef } from 'react'
import { Shield, ShieldCheck, ShieldOff, Loader2, Copy, Check, X, AlertCircle } from 'lucide-react'
import { Button } from '../atoms'
import { useTwoFactorStatus, useSetup2FA, useVerify2FA, useDisable2FA } from '../../hooks/useTwoFactor'
import { useSuccessToast, useErrorToast } from '../atoms/Toaster'

type Stage = 'idle' | 'setup' | 'verify' | 'backupCodes' | 'disable'

export function TwoFactorSection() {
  const statusQuery = useTwoFactorStatus()
  const setupMutation = useSetup2FA()
  const verifyMutation = useVerify2FA()
  const disableMutation = useDisable2FA()
  const showSuccess = useSuccessToast()
  const showError = useErrorToast()

  const [stage, setStage] = useState<Stage>('idle')
  const [secret, setSecret] = useState<string | null>(null)
  const [qrCodeDataUrl, setQrCodeDataUrl] = useState<string | null>(null)
  const [code, setCode] = useState('')
  const [backupCodes, setBackupCodes] = useState<string[]>([])
  const [copied, setCopied] = useState(false)
  const [confirmCode, setConfirmCode] = useState('')
  const setupTriggeredRef = useRef(false)

  const enabled = statusQuery.data?.enabled ?? false
  const remainingCodes = statusQuery.data?.remainingBackupCodes ?? 0

  // Disparar setup UNA sola vez cuando entramos a stage 'setup'.
  // Usamos un ref para evitar loop con el objeto setupMutation.
  useEffect(() => {
    if (stage !== 'setup') return
    if (setupTriggeredRef.current) return
    if (secret) return
    setupTriggeredRef.current = true
    setupMutation.mutate(undefined, {
      onSuccess: (data) => {
        setSecret(data.secret)
        setQrCodeDataUrl(data.qrCodeDataUrl)
      },
      onError: (err: any) => {
        setupTriggeredRef.current = false
        showError('Error', err?.message || 'No se pudo iniciar la configuración de 2FA')
      },
    })
  }, [stage])

  // Reset del trigger cuando salimos de 'setup'
  useEffect(() => {
    if (stage !== 'setup') {
      setupTriggeredRef.current = false
    }
  }, [stage])

  const handleCopy = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      showError('Error', 'No se pudo copiar al portapapeles')
    }
  }

  const handleStartSetup = () => {
    setSecret(null)
    setQrCodeDataUrl(null)
    setCode('')
    setupTriggeredRef.current = false
    setStage('setup')
  }

  const handleVerify = async () => {
    if (code.length < 6) return
    try {
      const result = await verifyMutation.mutateAsync(code.replace(/\s/g, ''))
      setBackupCodes(result.backupCodes)
      setStage('backupCodes')
      setCode('')
      showSuccess('2FA activado', 'Guarda los códigos de respaldo en un lugar seguro')
    } catch (error: any) {
      showError('Código inválido', error.message || 'Verifica el código e intenta de nuevo')
    }
  }

  const handleDisable = async () => {
    if (confirmCode.length < 6) return
    try {
      await disableMutation.mutateAsync(confirmCode.replace(/\s/g, ''))
      setStage('idle')
      setConfirmCode('')
      setSecret(null)
      setQrCodeDataUrl(null)
      showSuccess('2FA desactivado', 'Tu cuenta ya no requiere verificación en 2 pasos')
    } catch (error: any) {
      showError('Código inválido', error.message || 'No se pudo desactivar 2FA')
    }
  }

  const handleCancelSetup = () => {
    setStage('idle')
    setSecret(null)
    setQrCodeDataUrl(null)
    setCode('')
    setupTriggeredRef.current = false
  }

  if (stage === 'idle') {
    return (
      <div className="space-y-4">
        <div className="flex items-start justify-between">
          <div>
            <h3 className="text-base font-semibold text-gray-900">Verificación en 2 pasos (2FA)</h3>
            <p className="text-sm text-gray-500 mt-1">
              Añade una capa extra de seguridad requiriendo un código de tu app autenticadora al iniciar sesión.
            </p>
          </div>
          {enabled ? (
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-emerald-50 text-emerald-700 border border-emerald-200">
              <ShieldCheck className="w-3.5 h-3.5" /> Activado
            </span>
          ) : (
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600 border border-gray-200">
              <ShieldOff className="w-3.5 h-3.5" /> Desactivado
            </span>
          )}
        </div>

        {enabled && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
            <p className="text-xs text-amber-800">
              <strong>Códigos de respaldo restantes:</strong> {remainingCodes} de 10. Si pierdes tu app autenticadora, estos códigos son tu única forma de acceder.
            </p>
          </div>
        )}

        {enabled ? (
          <Button
            variant="outline"
            onClick={() => setStage('disable')}
            className="text-red-600 border-red-200 hover:bg-red-50"
          >
            <ShieldOff className="w-4 h-4 mr-2" />
            Desactivar 2FA
          </Button>
        ) : (
          <Button onClick={handleStartSetup}>
            <Shield className="w-4 h-4 mr-2" />
            Activar 2FA
          </Button>
        )}
      </div>
    )
  }

  if (stage === 'setup' || stage === 'verify') {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-base font-semibold text-gray-900">Configurar 2FA</h3>
          <button
            onClick={handleCancelSetup}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
          <p className="text-xs text-blue-800">
            <strong>Paso {stage === 'setup' ? '1' : '2'} de 2.</strong>{' '}
            {stage === 'setup'
              ? 'Escanea el QR con Google Authenticator, Microsoft Authenticator o Authy.'
              : 'Ingresa el código de 6 dígitos que muestra tu app para confirmar.'}
          </p>
        </div>

        {setupMutation.isPending && !qrCodeDataUrl && (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-primary-600" />
          </div>
        )}

        {qrCodeDataUrl && secret && (
          <div className="space-y-4">
            <div className="flex flex-col items-center gap-3 p-4 bg-white border border-gray-200 rounded-lg">
              <img src={qrCodeDataUrl} alt="QR para 2FA" className="w-48 h-48" />
              <div className="text-center">
                <p className="text-xs text-gray-500 mb-1">O ingresa este código manualmente:</p>
                <code className="text-xs font-mono bg-gray-100 px-2 py-1 rounded select-all">{secret}</code>
                <button
                  onClick={() => handleCopy(secret)}
                  className="ml-2 text-gray-400 hover:text-primary-600"
                  title="Copiar"
                >
                  {copied ? <Check className="w-3.5 h-3.5 text-emerald-500 inline" /> : <Copy className="w-3.5 h-3.5 inline" />}
                </button>
              </div>
            </div>

            {stage === 'verify' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Código de 6 dígitos
                </label>
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={6}
                  placeholder="000000"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  className="block w-full text-center text-xl font-mono tracking-[0.4em] px-3 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  autoFocus
                />
                <div className="mt-3 flex justify-end gap-2">
                  <Button variant="outline" onClick={() => { setStage('setup'); setCode('') }}>
                    Atrás
                  </Button>
                  <Button onClick={handleVerify} isLoading={verifyMutation.isPending} disabled={code.length < 6}>
                    Verificar y activar
                  </Button>
                </div>
              </div>
            )}

            {stage === 'setup' && (
              <div className="flex justify-end">
                <Button onClick={() => setStage('verify')}>
                  Ya lo escaneé, continuar
                </Button>
              </div>
            )}
          </div>
        )}
      </div>
    )
  }

  if (stage === 'backupCodes') {
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-2 text-emerald-700">
          <ShieldCheck className="w-5 h-5" />
          <h3 className="text-base font-semibold">¡2FA activado correctamente!</h3>
        </div>

        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
          <div className="flex items-start gap-2">
            <AlertCircle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
            <div className="text-xs text-amber-800">
              <p className="font-semibold">¡Importante! Guarda estos códigos en un lugar seguro.</p>
              <p className="mt-1">
                Si pierdes acceso a tu app autenticadora, estos códigos de un solo uso son tu única forma de entrar a tu cuenta.
              </p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-2">
          {backupCodes.map((c) => (
            <code
              key={c}
              className="text-center font-mono text-sm bg-gray-50 border border-gray-200 rounded px-3 py-2 select-all"
            >
              {c}
            </code>
          ))}
        </div>

        <div className="flex justify-between">
          <Button
            variant="outline"
            onClick={() => handleCopy(backupCodes.join('\n'))}
          >
            <Copy className="w-4 h-4 mr-2" />
            {copied ? '¡Copiado!' : 'Copiar todos'}
          </Button>
          <Button onClick={() => { setStage('idle'); setBackupCodes([]) }}>
            He guardado mis códigos
          </Button>
        </div>
      </div>
    )
  }

  if (stage === 'disable') {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-base font-semibold text-gray-900">Desactivar 2FA</h3>
          <button
            onClick={() => { setStage('idle'); setConfirmCode('') }}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="bg-red-50 border border-red-200 rounded-lg p-3">
          <p className="text-xs text-red-800">
            <strong>Advertencia:</strong> Al desactivar 2FA, tu cuenta será más vulnerable. Ingresa un código de tu app o un código de respaldo para confirmar.
          </p>
        </div>

        <label className="block text-sm font-medium text-gray-700 mb-2">
          Código de verificación
        </label>
        <input
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          maxLength={9}
          placeholder="000000 o XXXX-XXXX"
          value={confirmCode}
          onChange={(e) => setConfirmCode(e.target.value)}
          className="block w-full text-center text-lg font-mono tracking-[0.3em] px-3 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          autoFocus
        />

        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={() => { setStage('idle'); setConfirmCode('') }}>
            Cancelar
          </Button>
          <Button
            onClick={handleDisable}
            isLoading={disableMutation.isPending}
            disabled={confirmCode.length < 6}
            className="bg-red-600 hover:bg-red-700"
          >
            Desactivar 2FA
          </Button>
        </div>
      </div>
    )
  }

  return null
}
